import { ApiCompatibilityService } from './core/services/api-compatibility.service';
import { inject as injectApi } from '@angular/core';
import { ReportsService as ReportsApi } from './shared/api/generated/reports/reports.service';
import { MaintenanceService } from './core/services/maintenance.service';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppStore } from './state/app.repository';
import { AppService } from './core/services/app.service';
import { AsyncPipe } from '@angular/common';
import { OdinConnectionStatus, OdinService } from './core/services/odin.service';
import { MainNav } from './core/components/header/main-nav/main-nav';
import { ViewOutlet } from './core/components/view-outlet/view-outlet';
import { Dock } from './core/components/dock/dock';
import { AppConfigStore } from './state/app-config.repository';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { IconBtn } from './shared/components/ui/buttons/icon-btn/icon-btn';
import { faClose } from '@fortawesome/pro-duotone-svg-icons';
import { UserService } from './core/services/user.service';
import {
  isBlinkBrowser,
  isFirefoxBrowser,
  isSafariBrowser,
  isSharedArrayBufferCapable,
  sleep,
} from '@4players/odin-common';
import { Button } from './shared/components/ui/buttons/gradient-btn/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DeviceService } from './core/services/device.service';
import { LocalStreamService } from './core/services/local-stream.service';
import { MediaPermissionsService } from './core/services/media-permissions.service';
import { UserSettingsStore } from './state/user-settings.repository';
import { ReconnectingOverlay } from './core/components/reconnecting-overlay/reconnecting-overlay';
import { PingIndicatorTiny } from './core/components/ping-indicator-tiny/ping-indicator-tiny';
import { base64 } from './shared/helpers/encoding';
import { HttpClient } from '@angular/common/http';
import { distinctUntilChanged, firstValueFrom, map, of, switchMap } from 'rxjs';
import { wallpaper$ } from './shared/helpers/wallpaper';
import { config } from '../environments/environment';
import { AppConfigService } from './core/services/app-config.service';
import { DevConsoleService } from './core/services/dev-console.service';
import { PermissionTutorial } from './shared/components/ui/permission-tutorial/permission-tutorial';
import { PromoButton } from './shared/components/ui/promo-button/promo-button';
import { SetupWizard } from './features/setup-wizard';
import { AppReleaseService } from './core/services/app-release.service';
import { TranscriptService } from './core/services/transcript.service';
import { AppUpdateNotice } from './features/pre-join/app-update-notice/app-update-notice';
import { isTauri } from './shared/helpers/platform';
import { nativeTenantDomain } from './shared/helpers/tenant';
import { SpaceSheet } from './features/spaces/space-sheet/space-sheet';
import { SquareIconBtn } from './shared/components/ui/buttons/square-icon-btn/square-icon-btn';
import { faArrowRightArrowLeft } from '@fortawesome/pro-duotone-svg-icons';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { ConnectionOverview } from './core/components/connection-overview/connection-overview';
import { ConnectionStatsService } from './core/services/connection-stats.service';
import { StreamHealthService } from './core/services/stream-health.service';
import { StreamStats } from './features/video/stream-stats/stream-stats';
import { StreamStatsDialogService } from './features/video/stream-stats/stream-stats-dialog.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    TranslatePipe,
    AsyncPipe,
    MainNav,
    ViewOutlet,
    Dock,
    IconBtn,
    FaDuotoneIconComponent,
    Button,
    ReconnectingOverlay,
    PingIndicatorTiny,
    PermissionTutorial,
    PromoButton,
    SetupWizard,
    AppUpdateNotice,
    SpaceSheet,
    SquareIconBtn,
    ConnectionOverview,
    FaIconComponent,
    StreamStats,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  protected readonly apiCompatibility = injectApi(ApiCompatibilityService);
  private readonly reportsApi = injectApi(ReportsApi);
  readonly #assets = injectApi(AppConfigStore);

  /**
   * The configured wallpaper for the current color mode: the sharp `<img>`
   * and, as `--app-background-image`, the statically blurred copies behind
   * the stage and the dock. Served from a same-origin blob (see `wallpaper$`).
   */
  protected readonly wallpaper = toSignal(
    injectApi(UserSettingsStore).colorMode$.pipe(
      switchMap((mode) => this.#assets.getAsset$(`${mode}Background`)),
      map((asset) => asset?.url && new URL(asset.url, document.baseURI).href),
      distinctUntilChanged(),
      switchMap((url) => (url ? wallpaper$(url) : of(null)))
    )
  );

  protected readonly OdinConnectionStatus = OdinConnectionStatus;
  protected readonly faClose = faClose;
  protected readonly isTauri = isTauri;
  protected readonly faArrowRightArrowLeft = faArrowRightArrowLeft;
  protected readonly connectionStats = injectApi(ConnectionStatsService);
  protected readonly streamHealth = injectApi(StreamHealthService);
  protected readonly streamStatsDialog = injectApi(StreamStatsDialogService);
  protected readonly faCopy = faCopy;
  protected readonly diagnosticsCopied = signal(false);
  #diagnosticsCopiedTimer?: ReturnType<typeof setTimeout>;
  /** Native app without any space: it can't start until one is added. */
  protected readonly spaceRequired = isTauri && !nativeTenantDomain();
  protected readonly spaceSheetOpen = signal(this.spaceRequired);
  title = 'andor';
  peerId = signal<number | null>(null);
  browser = signal('Unknown');
  config = config;
  appReady = signal(false);
  #initialization?: Promise<void>;
  #mediaInitialized = false;
  submittingReport = signal(false);

  get sharedArrayBuffer() {
    return isSharedArrayBufferCapable();
  }

  // False means the COOP/COEP headers are missing on the document response, so
  // the SDK silently degrades to the non-SharedArrayBuffer audio path.
  get crossOriginIsolated() {
    return self.crossOriginIsolated;
  }

  get serverAddress() {
    return this.odinService.room?.address;
  }

  get customerId() {
    return this.odinService.room?.customer;
  }

  get roomId() {
    return this.odinService.room?.id;
  }

  constructor(
    public maintenance: MaintenanceService,
    public appStore: AppStore,
    public appConfigStore: AppConfigStore,
    public odinService: OdinService,
    public appService: AppService,
    public userService: UserService,
    public deviceService: DeviceService,
    public localStream: LocalStreamService,
    public permissions: MediaPermissionsService,
    public userSettings: UserSettingsStore,
    private _appConfigService: AppConfigService,
    private _translateService: TranslateService,
    private _snackBar: MatSnackBar,
    private _httpClient: HttpClient,
    // Injected eagerly so its constructor runs at app bootstrap — that's
    // what installs the `window.chat` console namespace on dev builds.
    _devConsole: DevConsoleService,
    // Listen before any pre-join or meeting views are mounted.
    _appRelease: AppReleaseService,
    // Collects live transcript lines from the bot's room frames for the
    // whole meeting, whether or not the transcription tab is open.
    _transcripts: TranscriptService
  ) {
    this.maintenance.recoveryRequested.subscribe((recovery) => {
      void (async () => {
        try {
          await this.#initialization;
          const roomId = this.odinService.currentRoomId || undefined;
          await this.appService.restoreAfterMaintenance(recovery, roomId);
          if (!this.maintenance.completeRecovery(recovery.generation)) return;
          if (!roomId && !this.odinService.room) {
            if (this.appService.routeStartupLanding() || !this.appService.isReady()) return;
            if (this.appService.autoJoin) await this._tryAutoJoin();
            else await this.handlePreparationPage();
          }
        } catch (error) {
          this.maintenance.failRecovery(recovery.generation, error);
        }
      })();
    });
    this.init().then();
    // Track the own user's current ODIN peerId for telemetry. The entity
    // is stable across reconnects; only the peerId field swaps in place,
    // so reading from ownUser$ is the reliable source.
    userService.ownUser$.subscribe((user) => {
      this.peerId.set(user?.peerId ?? null);
    });
    if (isSafariBrowser()) {
      this.browser.set('Safari');
    }
    if (isFirefoxBrowser()) {
      this.browser.set('Firefox');
    }
    if (isBlinkBrowser()) {
      this.browser.set('Blink');
    }
  }

  /** Connection stats and the health of every watched video stream. */
  private streamDiagnostics() {
    return {
      connection: this.connectionStats.diagnostics(),
      streams: this.streamHealth.diagnostics(),
    };
  }

  async copyDiagnostics() {
    const diagnostics = {
      at: new Date().toISOString(),
      version: this.appService.version,
      userAgent: navigator.userAgent,
      server: this.serverAddress,
      roomId: this.roomId,
      peerId: this.peerId(),
      ...this.streamDiagnostics(),
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
    } catch {
      return;
    }
    this.diagnosticsCopied.set(true);
    clearTimeout(this.#diagnosticsCopiedTimer);
    this.#diagnosticsCopiedTimer = setTimeout(() => this.diagnosticsCopied.set(false), 2000);
  }

  async sendReport() {
    if (this.submittingReport()) return;
    this.submittingReport.set(true);

    const audioDevicePermissionState = this.permissions.microphoneAccess();
    const videoDevicePermissionState = this.permissions.cameraAccess();

    // TODO: expand if needed
    const report = {
      appEnvironment: {
        userAgent: navigator?.userAgent ?? 'Unknown',
        location: window?.location?.href ?? 'Unknown',
        platform: this.browser(),
        version: this.appService.version,
        crossOriginIsolated: this.crossOriginIsolated,
        sharedArrayBuffer: this.sharedArrayBuffer,
        devices: {
          audio: {
            permission: audioDevicePermissionState,
            inputDevices:
              audioDevicePermissionState === 'granted'
                ? (await this.deviceService.audioInputDevices$.getValue()).map((device) => ({
                    id: device.id,
                    name: device.name,
                    default: device.isDefault,
                    active: device.id === this.deviceService.activeAudioInputDevice?.id,
                  }))
                : [],
            outputDevices:
              audioDevicePermissionState === 'granted'
                ? (await this.deviceService.audioOutputDevices$.getValue()).map((device) => ({
                    id: device.id,
                    name: device.name,
                    default: device.isDefault,
                    active: device.id === this.deviceService.activeAudioOutputDevice?.id,
                  }))
                : [],
          },
          video: {
            permission: videoDevicePermissionState,
            inputDevices:
              videoDevicePermissionState === 'granted'
                ? (await this.deviceService.getVideoDevices()).map((device) => ({
                    id: device.deviceId,
                    name: device.label,
                    default: false,
                    active: device.deviceId === this.deviceService.activeVideoDeviceId,
                  }))
                : [],
            outputDevices: [], // intentionally empty
          },
        },
      },
      odinConnctionState: {
        customer: this.customerId,
        server: this.serverAddress,
        peerId: this.peerId(),
        roomId: this.roomId,
        stats: this.odinService.room?.connectionStats,
        peers: Array.from(this.odinService.room?.peers.entries() ?? []).map(([peerId, peer]) => ({
          id: peerId,
          medias: peer.isRemote ? peer.audioOutputs.map((output) => output.mediaId) : [],
          active: peer.isActive,
          remote: peer.isRemote,
          volume: peer.volume,
        })),
      },
      odinMediaState: {
        audioInputs: (this.odinService.room?.audioInputs ?? []).map((input) => ({
          kind: input.kind,
          isActive: input.isActive,
          volume: input.volume,
          powerLevel: input.powerLevel,
          customType: input.customType,
        })),
        audioOutputs: (this.odinService.room?.audioOutputs ?? []).map((output) => ({
          uid: output.uid,
          kind: output.kind,
          mediaId: output.mediaId,
          peerId: output.peer?.id,
          active: output.isActive,
          paused: output.isPaused,
          customType: output.customType,
          volumeConfigured: output.volume,
          volumeAggregated: output.volumeAggregated,
          powerLevel: output.powerLevel,
        })),
        videoInputs: {
          camera: this.localStream.camStream
            ? {
                active: this.localStream.camStream.active,
                tracks: this.localStream.camStream.getTracks().map((t) => ({
                  kind: t.kind,
                  label: t.label,
                  enabled: t.enabled,
                  muted: t.muted,
                  readyState: t.readyState,
                  settings: t.getSettings(),
                })),
              }
            : undefined,
          screenShare: this.localStream.screenCastStream
            ? {
                active: this.localStream.screenCastStream.active,
                tracks: this.localStream.screenCastStream.getTracks().map((t) => ({
                  kind: t.kind,
                  label: t.label,
                  enabled: t.enabled,
                  muted: t.muted,
                  readyState: t.readyState,
                  settings: t.getSettings(),
                })),
              }
            : undefined,
          backgroundBlur: {
            enabled: this.localStream.backgroundBlurEnabled,
            amount: this.localStream.backgroundBlurAmount,
          },
        },
        videoOutputs: (this.odinService.room?.videoOutputs ?? []).map((output) => ({
          uid: output.uid,
          kind: output.kind,
          mediaId: output.mediaId,
          peerId: output.peer?.id,
          active: output.isStarted,
          paused: output.isPaused,
          customType: output.customType,
          tracks: output.mediaStream
            ? output.mediaStream.getTracks().map((t) => ({
                kind: t.kind,
                label: t.label,
                enabled: t.enabled,
                muted: t.muted,
                readyState: t.readyState,
                settings: t.getSettings(),
              }))
            : [],
        })),
      },
      odinPluginState: JSON.parse(String(await window.odin_debug_dump_state())),
      streamDiagnostics: this.streamDiagnostics(),
    };

    const payload = {
      roomId: this.roomId ?? '',
      sender: `${this.userService.ownUser()?.data.name} (id:${this.peerId()})`,
      report: base64(JSON.stringify(report)),
    };

    try {
      const request = this.reportsApi.submitReport(payload);
      const result = await firstValueFrom(request);
      console.log('submitted report', result);

      this._snackBar.open(
        this._translateService.instant('Report submitted successfully!'),
        undefined,
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        }
      );
    } catch (e) {
      console.error(e);
    }

    await sleep(5000); // quick-and-dirty
    this.submittingReport.set(false);
  }

  init(): Promise<void> {
    this.#initialization ??= this.#initialize().finally(() => {
      this.#initialization = undefined;
    });
    return this.#initialization;
  }

  async #initialize(): Promise<void> {
    this.appReady.set(false);
    // Nothing to start against yet; the space sheet is the whole app.
    if (this.spaceRequired) return;
    if (!this.#mediaInitialized) {
      this.#mediaInitialized = true;
      void this.odinService.init().catch((e) => console.error('[init] odin init failed', e));
    }
    try {
      await this.appService.init();
      if (this.maintenance.active() || this.appService.routeStartupLanding()) return;
      if (!this.appService.isReady()) return;
      if (this.appService.autoJoin) {
        await this._tryAutoJoin();
      } else {
        await this.handlePreparationPage();
      }
    } finally {
      this.appReady.set(true);
    }
  }

  // Fired once on startup if `?autoJoin=1` was on the entry URL. Reads
  // roomName + userName straight from settings (both may have been seeded by
  // the URL parser in `AppService`, or by a prior persisted session) and
  // calls `OdinService.connect()` directly — same code path the
  // RoomLogin / PreparationArea buttons take.
  private async _tryAutoJoin(): Promise<void> {
    const { userName, roomId } = this.userSettings.value;
    if (!userName || !roomId) {
      console.warn(`[autoJoin] skipped — missing ${!userName ? 'userName' : 'roomId'}`);
      this.appService.showFailedConnectFallback();
      return;
    }
    try {
      // Same open-on-demand semantics as the prep-area Join button: an autoJoin
      // invite link to an instant room (not in the registry) opens it on the
      // fly; the server ignores the flag for a room that already exists. But
      // first resolve the id like the prep area does — the server maps an old
      // link carrying a slugified room NAME onto the matching registry room —
      // so local state (settings, password key) tracks the real room instead
      // of the slug before connecting.
      const resolved = await this.appService.resolveInviteRoom(roomId);
      const targetId = resolved?.roomId ?? roomId;
      const roomName = resolved?.name ?? (this.userSettings.value.roomName || roomId);
      const success = await this.odinService.connect(targetId, undefined, false, {
        createIfMissing: true,
        roomName,
      });
      if (!success) {
        console.warn('[autoJoin] connect returned false');
        this.appService.showFailedConnectFallback();
      }
    } catch (e) {
      console.error('[autoJoin] connect failed', e);
      this.appService.showFailedConnectFallback();
    }
  }

  closeMenus() {
    this._appConfigService.closeSettings();
    this.appStore.update({
      appInfoExpanded: false,
    });
  }

  async handlePreparationPage() {
    if (!this.appService.isReady() || this.appService.hasPendingRecovery) {
      return;
    }
    try {
      // Section/logo mapping is shared with the failed-connect fallback via
      // `preJoinViewState()`. Note: this path must NOT touch `isConnectedView`
      // — during a `persistAppState` reload it is still `true` and is what
      // triggers the auto-reconnect; resetting it here would cancel that.
      this.appStore.update(this.appService.preJoinViewState());
    } catch (e) {
      console.error(e);
    }
    this.userSettings.update({ requestPermissionsCheck: false });
  }
}
